import { enumTransformer } from '../../../helpers/object-utils';
import {
    FirmsInstrumentsResponse,
    IFirm,
    IFirmInstrumentInfo,
    InstrumentType,
} from 'sfx-web-contract';
import { ICcypair } from '@alk/currency';
import {
    IClient,
    ICurrencyHoliday,
    IFirmsAndInstrumentsMessageResponse,
    IInstrument,
    IStreamData,
} from '../../../interfaces';
import { SymbolTypes } from '@fnx/fx-pad';

const instrumentTypeMapper = new Map<InstrumentType, SymbolTypes>([
    [ InstrumentType.INSTRUMENTTYPE_SPOT, 'spot' ],
    [ InstrumentType.INSTRUMENTTYPE_FORWARD, 'forward' ],
]);

const instrumentType2SymbolTypes: (instrumentType: InstrumentType) => SymbolTypes =
    enumTransformer('instrument type', instrumentTypeMapper);

export function instrumentsTransformator(instruments: Array<IFirmInstrumentInfo> = []): Array<IInstrument> {
    return Boolean(instruments) ?
        instruments.map(instr => ({
            symbol: instr.Symbol as ICcypair,
            fractionSize: 4, //TODO: use value from server when it'll be available
            minTradeQty: instr.MinTradeVolume,
            roundLot: instr.RoundLot,
            extraDigital: true, //TODO: use value from server when it'll be available
            holidays: instr.Holidays.map(((holiday) => {
                return { date: holiday.Date, reason: holiday.Reason } as ICurrencyHoliday;
            })),
            productType: instrumentType2SymbolTypes(instr.InstrumentType),
            settlementPeriod: instr.SettlementPeriod,
            quotesTrendOnly: instr.QuotesTrendOnly,
        })) :
        [];
}

export function firmsTransformator(firms: Array<IFirm> = []): Array<IClient> {
    return Boolean(firms) ?
        firms.map(firm => ({
            id: firm.Id,
            name: firm.Name,
            inHouse: firm.InHouse,
            externalRef: firm.ExternalRef?.value,
        })) :
        [];
}

export function buildKeyValueInstruments(firms: Array<IFirm>): Record<number, Array<IInstrument>> {
    if (Array.isArray(firms)) {
        return firms.reduce((acc, firm) => {
            return {
                ...acc,
                [ firm.Id ]: instrumentsTransformator(firm.Instruments),
            };
        }, {} as Record<number, Array<IInstrument>>);
    } else {
        return {};
    }
}

export function decodeFirmsAndInstrumentsResponse(
    response: IStreamData<Uint8Array>,
): IFirmsAndInstrumentsMessageResponse {
    const {
        Firms,
        SelectedFirmId,
    } = FirmsInstrumentsResponse.decode(response.payload);
    const clients = firmsTransformator(Firms);
    const instrumentsMap = buildKeyValueInstruments(Firms);

    return {
        clients,
        instrumentsMap,
        attachedClientId: SelectedFirmId,
    };
}
